
import Vue from 'vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import TeamOverview from '@/views/Team/TeamDashboard/TeamOverview.vue';
import TeamMembersManagement from '@/views/Team/TeamDashboard/TeamMembersManagement.vue';
import PageTopNavigation from '@/components/base/PageTopNavigation.vue';
import SpacesList from '@/views/Team/SpacesList.vue';
import TeamRunnitSettings from '@/views/Team/TeamRunnitSettings.vue';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'TeamDashboard',
	metaInfo () {
		return {
			title: `Dashboard | ${this.currTeam?.name || this.displayLabels.team} | RunDiffusion`,
		};
	},
	mixins: [
		TeamUserMixin,
	],
	data () {
		const TAB: Record<string, string> = {
			HOME: 'HOME',
			RD: 'RD',
			RUNNIT: 'RUNNIT',
		};
		return {
			loadingCurrTeam: false,
			currTeam: null,

			loadingCurrTeamUserRole: false,
			currTeamUserRole: null,

			loadingTeamUserRoleDefs: false,
			teamUserRoleDefs: [],
			loadingLocalBalanceAccount: false,
			localBalanceAccount: null,

			TAB,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		...mapGetters([
			'displayLabels',
		]),
		selectedTab () {
			return (this.$route.query?.tab && this.TAB[this.$route.query.tab]) || this.TAB.HOME;
		},
		// Permission checks must run against the team this page is showing (route teamId),
		// not the signed-in team in the store. A company admin viewing another team's
		// dashboard would otherwise see their own team's RD_ADMIN answer here.
		hasCurrTeamRdAdminPermission (): boolean {
			if (!this.currTeam) return false;
			return this.checkRdAdminPermission(this.currTeam, this.currTeamUserRole);
		},
		tabsData () {
			return [
				{
					text: 'Members',
					route: {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
						},
					},
				} as any,
				{
					text: 'Open-Source Apps Settings',
					route: {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
						},
						query: {
							tab: this.TAB.RD,
						},
					},
				} as any,
				...(this._get(this.currTeam, 'company.clubOffer.benefits.isEnterprise') && this.hasManageCompanyPermission ? [
					{
						text: 'Generate Settings',
						route: {
							name: ROUTER.TEAM_DASHBOARD,
							params: {
								teamId: this.$route.params.teamId,
							},
							query: {
								tab: this.TAB.RUNNIT,
							},
						},
					} as any,
				] : []),
			];
		},
	},
	watch: {
		'$route.params.teamId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					this.bindCurrTeamAndTeamUserRole(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		async bindCurrTeamAndTeamUserRole (teamId: string) {
			const routeAway = () => {
				this.routerReplace(this.$route, this.$router, {
					name: ROUTER.COMPANY_DASHBOARD,
				});
			};
			if (!teamId) routeAway();

			try {
				this.loadingCurrTeam = true;
				this.loadingCurrTeamUserRole = true;

				const userTeamRoleId: string = `${teamId}_${this.user.id}`;
				await Promise.all([
					this.$bind(
						'currTeam',
						db.doc(`teams/${teamId}`),
						get$bindFirestoreOptions(),
					),
					this.$bind(
						'currTeamUserRole',
						db.doc(`teamUserRoles/${userTeamRoleId}`),
						get$bindFirestoreOptions(),
					),
				]);

				this.bindTeamUserRoleDefs(this.currTeam?.companyId);
				this.bindLocalBalanceAccount(teamId);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading ${this.displayLabels.team}`,
					show: true,
				});
				routeAway();
			} finally {
				this.loadingCurrTeam = false;
				this.loadingCurrTeamUserRole = false;
			}
		},
		async bindLocalBalanceAccount (teamId: string) {
			if (!teamId) return;

			try {
				this.localBalanceAccount = null;
				this.loadingLocalBalanceAccount = true;
				await this.$bind(
					'localBalanceAccount',
					db
						.collection('balanceAccounts')
						.doc(teamId),
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding balance account data:', error);
			} finally {
				this.loadingLocalBalanceAccount = false;
			}
		},
		async bindTeamUserRoleDefs (companyId: string) {
			try {
				this.loadingTeamUserRoleDefs = true;
				if (!companyId) return;
				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const defsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');
				await this.$bind('teamUserRoleDefs', defsRef, get$bindFirestoreOptions());
			} catch (e) {
				console.error('Error binding team user role defs:', e);
			} finally {
				this.loadingTeamUserRoleDefs = false;
			}
		},
	},
	components: {
		TeamRunnitSettings,
		SpacesList,
		TeamOverview,
		TeamMembersManagement,
		PageTopNavigation,
	},
});
